import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermission } from '../../../context/PermissionContext';
import {
  Menu, X, Clock, BarChart2, UserCircle,
  LayoutDashboard, Briefcase, Building2, Eye,
  Users, UserPlus, Calendar, Monitor, AlertTriangle, Shield
} from 'lucide-react';

const LMSSidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { hasPermission } = usePermission();

  const navigationItems = [
    {
      category: 'General',
      items: [
        { name: 'Clock In', path: '/tiles/labor_management_system/general/clock-in', icon: <Clock className="w-5 h-5" />, permission: 'lms_clock_in' },
        { name: 'Hours & Stats', path: '/tiles/labor_management_system/general/hours-stats', icon: <BarChart2 className="w-5 h-5" />, permission: 'lms_hours_stats' },
        { name: 'Profile', path: '/tiles/labor_management_system/general/profile', icon: <UserCircle className="w-5 h-5" />, permission: 'lms_profile' }
      ]
    },
    {
      category: 'Administration',
      items: [
        { name: 'Dashboard', path: '/tiles/labor_management_system/administration/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, permission: 'lms_administration' },
        { name: 'Activities & Projects', path: '/tiles/labor_management_system/administration/activities-projects', icon: <Briefcase className="w-5 h-5" />, permission: 'lms_activities' },
        { name: 'Shifts & Teams', path: '/tiles/labor_management_system/administration/shifts-teams', icon: <Clock className="w-5 h-5" />, permission: 'lms_shifts_teams' },
        { name: 'Business Units', path: '/tiles/labor_management_system/administration/business-units', icon: <Building2 className="w-5 h-5" />, permission: 'lms_business_units' },
        { name: 'Birds Eye', path: '/tiles/labor_management_system/administration/birds-eye', icon: <Eye className="w-5 h-5" />, permission: 'lms_birds_eye' },
        { name: 'Alerts & Review', path: '/tiles/labor_management_system/administration/alerts-review', icon: <AlertTriangle className="w-5 h-5" />, permission: 'lms_administration' },
        { name: 'Emergency Roster', path: '/tiles/labor_management_system/administration/emergency-roster', icon: <Shield className="w-5 h-5" />, permission: 'lms_administration' }
      ]
    },
    {
      category: 'Groups',
      items: [
        { name: 'Command', path: '/tiles/labor_management_system/groups/command', icon: <Users className="w-5 h-5" />, permission: 'lms_command' },
        { name: 'Create & Assign', path: '/tiles/labor_management_system/groups/create-assign', icon: <UserPlus className="w-5 h-5" />, permission: 'lms_create_assign' }
      ]
    },
    {
      category: 'Time Management',
      items: [
        { name: 'My Time', path: '/tiles/labor_management_system/time-management/my-time', icon: <Calendar className="w-5 h-5" />, permission: 'lms_time_management' }
      ]
    },
    {
      category: 'Kiosk',
      items: [
        { name: 'Kiosk View', path: '/tiles/labor_management_system/kiosk/kiosk-view', icon: <Monitor className="w-5 h-5" />, permission: 'lms_kiosk' }
      ]
    }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
          className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 transform 
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          <Link 
            to="/"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 rounded-md"
          >
            <span className="mr-3">←</span>
            <span>Back to HQ</span>
          </Link>
        </div>

        <nav className="mt-4">
          {navigationItems.map((section) => {
            // Filter items based on permissions
            const permittedItems = section.items.filter(item => hasPermission(item.permission));
            
            // Only render the section if it has at least one permitted item
            if (permittedItems.length === 0) return null;
            
            return (
              <div key={section.category} className="mb-6">
                <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {section.category}
                </h3>
                <div>
                  {permittedItems.map(item => (
                    <Link 
                      key={item.path} 
                      to={item.path}
                      className={`flex items-center px-4 py-2 text-sm ${
                        isActive(item.path)
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-slate-100'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="mr-3">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
};

export default LMSSidebar;
